/**
 * Soft complexity gate for PAS hatch prompts (刻意练习孵化词).
 * Warns when the intent looks too abstract / multi-step for flash PKP —
 * never blocks generation.
 */

export type PasHatchComplexityLevel = 'ok' | 'elevated' | 'high';

export type PasHatchComplexityAssessment = {
  level: PasHatchComplexityLevel;
  score: number;
  reasons: string[];
  /** User-facing warning; empty when ok. */
  warning: string;
  suggestProModel: boolean;
};

const ABSTRACT_MARKERS = [
  /最大公约数|gcd\s*\(|lcm\s*\(/i,
  /任意\s*[Tt]\s*个|任意连续|任意长度/,
  /通项|一般情形|一般形式|参数化|抽象/,
  /同余|模\s*\d|模运算|群论|归纳法/,
  /证明|推导|引理|充分必要/,
  /周期\s*[Tt]1|周期\s*[Tt]2|子周期/,
  /融合到实际|逻辑融合|摸清.*特性/,
];

const MULTI_STEP_MARKERS = [
  /如果.+那么/,
  /进一步|然后再|同时要求|并且还要/,
  /先.+再.+然后/,
  /构造题目|出题要求|需要同时/,
];

function countSentences(text: string): number {
  const parts = text.split(/[。！？；;\n]+/).map((part) => part.trim()).filter(Boolean);
  return parts.length;
}

/**
 * Heuristic only — mirrors PKP 四原则压力（短/锋利），不调用 LLM。
 */
export function assessPasHatchComplexity(hatchPrompt: unknown): PasHatchComplexityAssessment {
  const prompt = String(hatchPrompt || '').replace(/\s+/g, ' ').trim();
  if (prompt.length < 2) {
    return {
      level: 'ok',
      score: 0,
      reasons: [],
      warning: '',
      suggestProModel: false,
    };
  }

  let score = 0;
  const reasons: string[] = [];

  if (prompt.length >= 220) {
    score += 3;
    reasons.push('孵化词很长，模型更难收成「短题干」变式');
  } else if (prompt.length >= 120) {
    score += 2;
    reasons.push('孵化词偏长，生成短题可能不稳定');
  }

  const sentenceCount = countSentences(prompt);
  if (sentenceCount >= 4) {
    score += 2;
    reasons.push('条件/句子较多，容易变成综合题而不是单点刻意练习');
  } else if (sentenceCount >= 3) {
    score += 1;
  }

  let abstractHits = 0;
  for (const pattern of ABSTRACT_MARKERS) {
    if (pattern.test(prompt)) abstractHits += 1;
  }
  if (abstractHits >= 2) {
    score += 3;
    reasons.push('含较多抽象/理论表述（如周期、公约数、一般情形），Flash 模型容易孵化失败');
  } else if (abstractHits === 1) {
    score += 2;
    reasons.push('含抽象概念，建议把目标收成一个可口算的小动作');
  }

  let multiStepHits = 0;
  for (const pattern of MULTI_STEP_MARKERS) {
    if (pattern.test(prompt)) multiStepHits += 1;
  }
  if (multiStepHits >= 2) {
    score += 2;
    reasons.push('步骤链较长，更像讲解大纲而不是锋利小能力');
  } else if (multiStepHits === 1) {
    score += 1;
  }

  if (/综合|多知识点|整章|系列大题/.test(prompt)) {
    score += 2;
    reasons.push('目标偏综合，偏离「只练一个小点」');
  }

  const level: PasHatchComplexityLevel = score >= 5
    ? 'high'
    : score >= 3
      ? 'elevated'
      : 'ok';

  if (level === 'ok') {
    return {
      level,
      score,
      reasons: [],
      warning: '',
      suggestProModel: false,
    };
  }

  const head = level === 'high'
    ? '当前孵化词可能偏复杂，普通模型有时生不出合格的刻意练习短题。'
    : '当前孵化词略复杂，生成短题可能不稳定。';
  const tip = level === 'high'
    ? '可继续生成；若多次失败，可改用 Pro 模型，或把目标收成「一个可口算的小动作」。'
    : '可继续生成；也可先把描述收短、只保留一个训练点。';

  return {
    level,
    score,
    reasons: reasons.slice(0, 3),
    warning: `${head}${reasons[0] ? `（${reasons[0]}）` : ''} ${tip}`,
    suggestProModel: level === 'high',
  };
}
